import { defineStore, storeToRefs } from "pinia"
import { ref, computed } from "vue"
import { getAudio } from "@/api/s3/index"
import { useHelpStore } from "@/components/af/Help/help.store"
import { voiceActor } from "@/features/voice/machines/voiceMachine"
import type { Ref } from "vue"
import type {
  Question,
  QuestionDetail,
} from "@/features/voice/store/voice.types"
import { useUserStore } from "@/stores/user"
import {
  getVoiceNextQuestion,
  getVoiceQuestions,
  postVoiceAudioQuestion,
  skipVoiceQuestion,
} from "@/api/viktor/assessment/assessment"
import { MixpanelInstance } from "@/plugins/mixpanel/mixpanel"
import { useSessionStorage } from "@/helpers/useSessionStorage"

export const useQuestionsStore = defineStore("voiceQuestionsStore", () => {
  const { getUserUuid, getVoiceUuid } = storeToRefs(useUserStore())
  const { getSessionItem } = useSessionStorage()

  const { canAssessmentBePassed } = storeToRefs(useHelpStore())

  const items: Ref<Question[]> = ref([])
  const currentQuestion: Ref<QuestionDetail | undefined> = ref()

  const questionsLength = computed(() => items.value.length)
  const questionsFinished = computed(
    () =>
      items.value.filter(
        (item) =>
          item.status === "finish" ||
          item.status === "skip" ||
          item.status === "waiting_scores"
      ).length
  )
  const isLastQuestion = computed(
    () => questionsFinished.value >= questionsLength.value - 1
  )

  const recruiter = computed(() => items.value[0].recruiter)

  const voiceIsFinish = computed(() => {
    return !items.value.some((item) => item.status === "todo")
  })

  async function fetchQuestions() {
    const data = await getVoiceQuestions(getUserUuid.value, getVoiceUuid.value)
    items.value = data.body

    return items.value
  }

  async function getNextQuestion() {
    const data = await getVoiceNextQuestion(
      getUserUuid.value,
      getVoiceUuid.value
    )
    currentQuestion.value = data
    return currentQuestion.value
  }

  async function submit(
    audio: Blob,
    suspiciousActivity?: Record<string, unknown>
  ) {
    const response = await postVoiceAudioQuestion(
      getUserUuid.value,
      getVoiceUuid.value,
      currentQuestion.value?.question.uuid as string,
      audio,
      suspiciousActivity
    )

    return response
  }

  async function skip() {
    MixpanelInstance.track("voice_question-skip")
    await skipVoiceQuestion(
      getUserUuid.value,
      getVoiceUuid.value,
      currentQuestion.value?.question.uuid as string
    )
  }

  function fetchAudios() {
    getAudio({ test: "voice" })
  }

  // if current question is not the last one of the list, increment the index
  async function goNext() {
    canAssessmentBePassed.value.voice = false // Disabled not passed assessment button

    if (isLastQuestion.value) {
      voiceActor.send({ type: "done" })
      return
    } else {
      await fetchQuestions()
      await getNextQuestion()
    }
  }

  const wasRedirectedToVoice = getSessionItem("wasRedirectedToVoice") === true

  return {
    questions: items.value,
    currentQuestion,
    questionsLength,
    questionsFinished,
    isLastQuestion,
    voiceIsFinish,
    recruiter,
    fetchQuestions,
    getNextQuestion,
    fetchAudios,
    submit,
    skip,
    goNext,
    wasRedirectedToVoice,
  }
})
